import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { formatTimestamp } from '../../utils/dataUtils';
import type { ReviewComment } from './types';

interface ReviewCommentsCardProps {
  reviewComments: ReviewComment[];
  commentDraft: string;
  setCommentDraft: (value: string) => void;
  commentSubmitting: boolean;
  onAddComment: () => void;
}

const ReviewCommentsCard: React.FC<ReviewCommentsCardProps> = ({
  reviewComments,
  commentDraft,
  setCommentDraft,
  commentSubmitting,
  onAddComment,
}) => {
  const hasComments = reviewComments.length > 0;

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Reviewer Comments
        </Typography>
        <Stack spacing={2}>
          <TextField
            label="Add reviewer comment"
            value={commentDraft}
            onChange={(event) => setCommentDraft(event.target.value)}
            multiline
            minRows={3}
            fullWidth
            placeholder="Capture review context, outstanding questions, or approval notes\u2026"
          />
          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              onClick={onAddComment}
              disabled={commentSubmitting || commentDraft.trim().length === 0}
            >
              {commentSubmitting ? 'Posting\u2026' : 'Post Comment'}
            </Button>
          </Box>
          {hasComments ? (
            <Stack spacing={1.5}>
              {reviewComments.map((comment) => (
                <Paper key={comment.comment_id} variant="outlined" sx={{ p: 1.5 }}>
                  <Stack spacing={0.5}>
                    <Typography variant="body2">{comment.message}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {comment.author} \u2022 {formatTimestamp(comment.created_at)}
                    </Typography>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No reviewer comments yet. Add the first note to kick off the review thread.
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default ReviewCommentsCard;
